import { useEffect } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Collaboration from '@tiptap/extension-collaboration';
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret';
import type { Awareness } from 'y-protocols/awareness';
import type * as Y from 'yjs';

import { sanitizeUrl } from '@/lib/identity';
import { cn } from '@/lib/utils';

interface DocEditorProps {
  ydoc: Y.Doc;
  awareness: Awareness;
  editable: boolean;
  userName?: string;
  userColor?: string;
  className?: string;
  /** Notifies the parent with the editor instance (used by the toolbar). */
  onEditor?: (editor: Editor | null) => void;
}

/**
 * The collaborative rich-text editor. Content lives in the shared Yjs
 * document — there is no local "content" prop; the CRDT state is the
 * source of truth and syncs automatically through the provider.
 */
export function DocEditor({
  ydoc,
  awareness,
  editable,
  userName = 'Anonymous',
  userColor = '#64748b',
  className,
  onEditor,
}: DocEditorProps) {
  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: [
        StarterKit.configure({
          // The Collaboration extension provides its own (per-user) history.
          undoRedo: false,
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: 'https',
          // Defense-in-depth on top of the DOM-level default validation:
          // only http(s)/mailto URLs may ever become links.
          isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && Boolean(sanitizeUrl(url)),
          HTMLAttributes: {
            rel: 'noopener noreferrer nofollow',
            target: '_blank',
            class: 'doc-link',
          },
        }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Placeholder.configure({
          placeholder: 'Start writing…',
        }),
        Collaboration.configure({ document: ydoc }),
        CollaborationCaret.configure({
          provider: { awareness, doc: ydoc },
          user: { name: userName, color: userColor },
          selectionRender: (user) => ({
            nodeName: 'span',
            class: 'collaboration-carets__selection',
            style: `background-color: ${user.color}33`,
            'data-user': String(user.name ?? ''),
          }),
        }),
      ],
      editable,
      editorProps: {
        attributes: {
          class: 'nostr-doc-content tiptap',
          spellcheck: 'true',
          'aria-label': 'Document content',
          role: 'textbox',
          'aria-multiline': 'true',
        },
      },
    },
    [ydoc, awareness],
  );

  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  useEffect(() => {
    onEditor?.(editor);
    return () => onEditor?.(null);
  }, [editor, onEditor]);

  return <EditorContent editor={editor} className={cn('w-full', className)} />;
}
